import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useFormik } from 'formik';
import { showErrorToast } from '@amigoapp/doca-react';
import PatientUpdateRegisterForm from './PatientUpdateRegisterForm';
import {
	findRegistration,
	updateRegistration,
	uploadInsuranceCardByRegistrationToken,
	deleteInsuranceCardByRegistrationToken
} from '../../processes/patient';
import { findSettingsByRegistrationToken, findScheduleSettingsByRegistrationToken } from '../../processes/company';
import { useCompanyStore, useAnswersStore, usePatientStore } from '../../stores';
import { useQuery, usePatientFileUpload } from '../../hooks';
import { DateUtils, NavigationUtils } from '../../utils';
import { formatPhone, formatZipcode, formatMonthYear, clearMask } from '../../utils/formatter';
import { MAX_PATIENT_DOCUMENT_FILE } from '../../constants/patientFiles';

const PAYMENT_METHODS = {
	CONVENIO: 'CONVENIO',
	PARTICULAR: 'PARTICULAR'
};

const REGISTRATION_FIELDS = [
	'name', 'social_name', 'born', 'cpf',
	'contact_cellphone', 'email',
	'address_cep', 'address_address', 'address_number', 'address_complement',
	'insurance_id', 'insurance_number', 'insurance_validate'
];

const BASE_REQUIRED_FIELDS = ['name', 'born', 'contact_cellphone', 'email', 'address_cep', 'address_number', 'address_address'];
const INSURANCE_REQUIRED_FIELDS = ['insurance_id', 'insurance_number', 'insurance_validate'];

const EMPTY_VALUES = REGISTRATION_FIELDS.reduce((acc, field) => ({ ...acc, [field]: '' }), {
	payment_method: PAYMENT_METHODS.PARTICULAR,
	confirm: false
});

const mapDataToValues = data => ({
	...REGISTRATION_FIELDS.reduce((acc, field) => ({ ...acc, [field]: data?.[field] ?? '' }), {}),
	born: data?.born ? DateUtils.formatDate(data.born, 'DD/MM/YYYY', 'YYYY-MM-DD') : '',
	contact_cellphone: formatPhone(data?.contact_cellphone || ''),
	address_cep: formatZipcode(data?.address_cep || '') || '',
	insurance_validate: formatMonthYear(data?.insurance_validate || ''),
	payment_method: data?.insurance_id ? PAYMENT_METHODS.CONVENIO : PAYMENT_METHODS.PARTICULAR,
	confirm: false
});

function PatientUpdateRegisterFormContainer() {
	const query = useQuery();
	const token = useMemo(() => query.get('token'), [query]);
	const history = useHistory();

	const { companySettings: { settings, scheduleSettings }, setCompanySettings } = useCompanyStore();
	const { clearAnswers } = useAnswersStore();
	const { clearPatientData } = usePatientStore();

	const [loading, setLoading] = useState(true);
	const [linkExpired, setLinkExpired] = useState(false);
	const [submitted, setSubmitted] = useState(false);
	const [insuranceCardFile, setInsuranceCardFile] = useState(null);

	const loadedValuesRef = useRef(EMPTY_VALUES);

	const insuranceCardFiles = useMemo(() => (insuranceCardFile ? [insuranceCardFile] : []), [insuranceCardFile]);

	const insuranceCard = usePatientFileUpload({
		maxFiles: MAX_PATIENT_DOCUMENT_FILE,
		currentFiles: insuranceCardFiles,
		errorToastMessage: 'Erro ao enviar a foto da carteirinha.',
		upload: useCallback(async files => {
			const { data, error } = await uploadInsuranceCardByRegistrationToken(files[0], token);

			if (error) {
				return { error };
			}

			return { data: [data] };
		}, [token]),
		onUploaded: useCallback(files => setInsuranceCardFile(files[0] || null), []),
		onRemove: useCallback(async () => {
			const { error } = await deleteInsuranceCardByRegistrationToken(token);

			if (error) {
				showErrorToast('Não foi possível remover a carteirinha. Tente novamente.');
				return;
			}

			setInsuranceCardFile(null);
		}, [token])
	});

	const handleSubmitPress = useCallback(async values => {
		const payload = REGISTRATION_FIELDS.reduce((acc, field) => {
			if (values[field] !== loadedValuesRef.current[field]) {
				acc[field] = values[field];
			}

			return acc;
		}, {});

		if (values.payment_method === PAYMENT_METHODS.PARTICULAR) {
			INSURANCE_REQUIRED_FIELDS.forEach(field => {
				delete payload[field];

				if (loadedValuesRef.current[field]) {
					payload[field] = null;
				}
			});
		}

		if (payload.born) {
			payload.born = DateUtils.formatDate(payload.born, 'YYYY-MM-DD', 'DD/MM/YYYY');
		}

		if (payload.insurance_validate) {
			payload.insurance_validate = DateUtils.formatDate(payload.insurance_validate, 'YYYY-MM-DD', 'MM/YYYY');
		}

		if (payload.contact_cellphone) {
			payload.contact_cellphone = clearMask(payload.contact_cellphone);
		}

		if (payload.address_cep) {
			payload.address_cep = clearMask(payload.address_cep);
		}

		setLoading(true);

		const { error } = await updateRegistration(payload, token);

		setLoading(false);

		if (error) {
			if (error?.code === 403) {
				setLinkExpired(true);
			} else if (error?.message === 'CELLPHONE_ALREADY_IN_USE') {
				showErrorToast('Esse telefone já está em uso por outro paciente. Verifique o número informado.');
			} else if (error?.message === 'INSURANCE_DOESNT_EXIST') {
				showErrorToast('Convênio selecionado inválido. Escolha o convênio novamente.');
			} else {
				showErrorToast('Não foi possível salvar seus dados. Tente novamente.');
			}

			return;
		}

		setSubmitted(true);
	}, [token]);

	const formik = useFormik({
		initialValues: EMPTY_VALUES,
		onSubmit: handleSubmitPress
	});

	const { handleChange, handleSubmit, setFieldValue, resetForm, values } = formik;

	const loadRegistration = useCallback(async () => {
		if (!token) {
			setLinkExpired(true);
			setLoading(false);

			return;
		}

		setLoading(true);

		const [{ data, error }, companySettingsResult, companySchedulerSettingsResult] = await Promise.all([
			findRegistration(token),
			findSettingsByRegistrationToken({ token }),
			findScheduleSettingsByRegistrationToken({ token })
		]);

		setCompanySettings({
			settings: companySettingsResult?.data,
			scheduleSettings: companySchedulerSettingsResult?.data
		});

		if (error) {
			if (error?.code === 403) {
				setLinkExpired(true);
			} else {
				showErrorToast('Não foi possível carregar seus dados cadastrais.');
			}

			setLoading(false);

			return;
		}

		if (!data) {
			clearAnswers();
			clearPatientData();

			NavigationUtils.navigate(history, `/patient-form?standalone=1&token=${token}`);

			return;
		}

		const loadedValues = mapDataToValues(data);

		loadedValuesRef.current = loadedValues;
		resetForm({ values: loadedValues });
		setInsuranceCardFile(data.insurance_card_file || null);

		setLoading(false);
	}, [token, resetForm, history, clearAnswers, clearPatientData, setCompanySettings]);

	useEffect(() => {
		loadRegistration();
	}, [loadRegistration]);

	const onChangeWithMask = useCallback((event, mask) => {
		if (event.target.value && mask) {
			event.target.value = mask(event.target.value);
		}

		return handleChange(event);
	}, [handleChange]);

	const onSelectInsurance = useCallback(insuranceId => {
		setFieldValue('insurance_id', insuranceId);
	}, [setFieldValue]);

	const onTogglePaymentMethod = useCallback(method => {
		setFieldValue('payment_method', method);
	}, [setFieldValue]);

	const isFieldPending = useCallback(field => {
		const requiredFields = values.payment_method === PAYMENT_METHODS.CONVENIO
			? [...BASE_REQUIRED_FIELDS, ...INSURANCE_REQUIRED_FIELDS]
			: BASE_REQUIRED_FIELDS;

		return requiredFields.includes(field) && !values[field];
	}, [values]);

	const pendingCount = useMemo(() => {
		const requiredFields = values.payment_method === PAYMENT_METHODS.CONVENIO
			? [...BASE_REQUIRED_FIELDS, ...INSURANCE_REQUIRED_FIELDS]
			: BASE_REQUIRED_FIELDS;

		return requiredFields.filter(field => !values[field]).length;
	}, [values]);

	const canSubmit = useMemo(() => !!values.confirm && pendingCount === 0, [values.confirm, pendingCount]);

	return (
		<PatientUpdateRegisterForm
			loading={loading}
			linkExpired={linkExpired}
			submitted={submitted}
			values={values}
			variant={settings?.theme?.name}
			companyName={settings?.name}
			companyLogoUrl={settings?.logo?.url}
			canSubmit={canSubmit}
			pendingCount={pendingCount}
			isFieldPending={isFieldPending}
			insuranceOptions={scheduleSettings?.insurance_plans || []}
			paymentMethod={values.payment_method}
			insuranceCard={insuranceCard}
			onChange={handleChange}
			onChangeWithMask={onChangeWithMask}
			onSelectInsurance={onSelectInsurance}
			onSubmitClick={handleSubmit}
			onTogglePaymentMethod={onTogglePaymentMethod}
		/>
	);
}

export default PatientUpdateRegisterFormContainer;
